import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { fetchJournal, fetchCollection, fetchSiteMeta } from "@/lib/api";
import { formatINR } from "@/lib/format";
import Seo from "@/components/seo/Seo";

const TICKER = [
  "Modern rituals",
  "Rooted in nature",
  "Topical. Ingestible. Sensory.",
  "Inside. Outside. Every day.",
  "Return to what still works",
];

const WIDE_BAND = "/generated/a-sweeping-wide-cinematic-overhead-edito-d3dfd0.png";
const BATH_SCENE = "/generated/a-warm-bath-scene-from-above-a-deep-sto-bd87e5.png";
const SHELF_SCENE = "/generated/an-open-shelf-in-warm-morning-light-thr-df4a2c.png";
const OLIVE_SCENE = "/generated/a-low-angle-still-life-on-a-deep-forest-e6ab9b.png";

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [restProducts, setRestProducts] = useState([]);
  const [heroImage, setHeroImage] = useState("/generated/hero-hero-a1bc74.png");

  useEffect(() => {
    fetchJournal().then(setArticles).catch(() => {});
    fetchCollection("rest-ritual")
      .then((d) => setRestProducts(d.products || []))
      .catch(() => {});
    fetchSiteMeta()
      .then((meta) => {
        const m = meta.hero || meta.default;
        if (m?.hero_image?.url) setHeroImage(m.hero_image.url);
      })
      .catch(() => {});
  }, []);

  const nourish = restProducts.find((p) => p.stage === "Nourish");
  const wash = restProducts.find((p) => p.stage === "Wash");
  const breathe = restProducts.find((p) => p.stage === "Breathe");
  const sip = restProducts.find((p) => p.stage === "Sip");
  const stageOrder = ["Wash", "Nourish", "Breathe", "Sip", "Daily"];
  const stepPreview = stageOrder
    .map((s) => restProducts.find((p) => p.stage === s))
    .filter(Boolean);

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title="Modern rituals, rooted in nature"
        description="Stillform creates modern wellness rituals for the body, mind and everyday life — drawing from botanicals, minerals and natural ingredients. Inside. Outside. Every day."
        image={nourish?.cover_image || heroImage}
      />

      {/* ───────── HERO ───────── */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 lg:px-16 bg-bone">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-6 lg:pb-6">
            <p className="overline mb-8 text-terracotta" data-testid="hero-overline">
              Stillform · Launching December 2026
            </p>
            <h1 className="font-display text-[3rem] sm:text-[4rem] md:text-[5rem] lg:text-[6.5rem] leading-[0.92] tracking-[-0.03em] font-light text-ink">
              Modern <em className="italic font-light text-terracotta">rituals,</em><br />
              rooted<br />
              in <span className="italic text-forest">nature.</span>
            </h1>
            <p className="mt-10 font-display italic text-xl md:text-2xl text-charcoal leading-snug max-w-md">
              Wellbeing isn't something you find.<br />
              It is something you return to.
            </p>
            <div className="mt-10 flex items-center gap-6 flex-wrap">
              <Link
                to="/rituals/rest"
                data-testid="hero-cta-rest"
                className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-cream bg-forest px-6 py-4 hover:bg-terracotta transition-colors"
              >
                Join the Rest waitlist
                <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/rituals"
                data-testid="hero-cta-rituals"
                className="text-[0.72rem] tracking-[0.22em] uppercase font-medium text-forest link-underline"
              >
                All rituals
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="img-zoom relative aspect-[4/5] md:aspect-[5/6] lg:aspect-[4/5] overflow-hidden bg-clay">
              <img
                src={nourish?.cover_image || heroImage}
                alt="Stillform — modern rituals rooted in nature"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 right-2 md:right-6 bg-cream px-4 py-3 max-w-[280px] hidden sm:block border border-terracotta/30">
              <p className="overline mb-1 text-terracotta">The Rest Ritual · 01</p>
              <p className="font-display italic text-charcoal text-base leading-snug">
                Coming December 2026 — join the waitlist for first access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── TICKER ───────── */}
      <section className="relative bg-terracotta py-6 md:py-8 overflow-hidden border-y border-ink/10">
        <div className="flex marquee-track whitespace-nowrap">
          {[...TICKER, ...TICKER, ...TICKER].map((w, i) => (
            <span key={i} className="font-display italic text-3xl md:text-5xl text-cream mx-10">
              {w} <span className="text-cream/50 not-italic font-body">— </span>
            </span>
          ))}
        </div>
      </section>

      {/* ───────── FULL-BLEED EDITORIAL BAND ───────── */}
      <section className="relative overflow-hidden bg-ink">
        <div className="aspect-[16/9] md:aspect-[16/7]">
          <img
            src={WIDE_BAND}
            alt="Stillform — the ritual, laid out"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16 py-10 md:py-14 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent">
            <p className="overline text-cream/80 mb-3">A table, slowly laid</p>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cream font-light italic leading-tight max-w-3xl">
              The ritual is not the thing on the shelf —<br className="hidden md:block" />
              it is the gesture you keep returning to.
            </h2>
          </div>
        </div>
      </section>

      {/* ───────── BRAND STORY — image + text + image collage ───────── */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-cream">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="img-zoom aspect-[4/5] overflow-hidden">
              <img
                src={SHELF_SCENE}
                alt="Stillform apothecary shelf"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <p className="overline mb-6 text-terracotta">Stillform</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-6xl font-light leading-[1.02] tracking-tight text-ink">
              Inside.<br />
              <em className="italic text-terracotta">Outside.</em><br />
              Every day.
            </h2>
            <div className="mt-8 space-y-6 text-charcoal text-base md:text-lg font-light leading-[1.75] max-w-xl">
              <p>
                Modern wellness rituals for the body, mind and everyday life —
                drawn from botanicals, minerals and natural ingredients, brought
                together through thoughtful formulation and contemporary science.
              </p>
              <p className="font-display italic text-ink text-xl md:text-2xl">
                Topical. Ingestible. Sensory. Ritual.
              </p>
            </div>
            <Link
              to="/philosophy"
              data-testid="philosophy-cta"
              className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-terracotta link-underline mt-10"
            >
              The philosophy <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── FEATURED PRODUCT MOMENT — Magnesium Body Butter ───────── */}
      {nourish && (
        <section className="relative bg-clay border-t border-hairline">
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[70vh]">
            <div className="relative aspect-square lg:aspect-auto overflow-hidden order-2 lg:order-1">
              <img
                src={nourish.cover_image}
                alt={nourish.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex items-center p-10 md:p-16 lg:p-20 order-1 lg:order-2">
              <div className="max-w-md">
                <p className="overline text-terracotta mb-6">The signature object · 02 Nourish</p>
                <h3 className="font-display text-4xl md:text-6xl font-light leading-[1.0] tracking-[-0.02em] text-ink">
                  Magnesium<br />
                  <em className="italic text-forest">Body Butter.</em>
                </h3>
                <p className="mt-8 font-display italic text-xl md:text-2xl text-charcoal leading-snug">
                  {nourish.tagline}
                </p>
                <p className="mt-6 text-charcoal font-light leading-[1.75]">
                  Dense, slow-melting. Shea, kokum and magnesium chloride, massaged
                  into the legs and shoulders after the wash. The mineral finds the
                  muscle. The body remembers.
                </p>
                <div className="mt-10 flex items-center gap-4 flex-wrap">
                  <p className="overline text-terracotta">Coming December 2026</p>
                  <Link
                    to="/rituals/rest"
                    data-testid="feature-cta"
                    className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-cream bg-forest px-6 py-3 hover:bg-terracotta transition-colors"
                  >
                    Join the waitlist <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ───────── RETURN TO WHAT WORKS — DEEP OLIVE PANEL ───────── */}
      <section className="relative overflow-hidden border-t border-forest/40 bg-forest text-cream">
        <div className="absolute inset-0 opacity-25 hidden md:block">
          <img src={OLIVE_SCENE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest/70" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-16 py-28 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="overline mb-6 text-terracotta-light">Our philosophy</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-7xl font-light leading-[1.0] tracking-[-0.02em] text-cream">
                Return to<br />
                what <em className="italic text-terracotta-light">still</em><br />
                works.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-7 text-cream/85 text-base md:text-lg font-light leading-[1.75]">
              <p>
                Long before wellness became an industry, people understood the
                value of plants, oils, minerals, warmth, water and rest.
              </p>
              <p>
                We are interested in that wisdom — not as something to preserve
                in the past, but something to bring forward.
              </p>
              <p className="font-display italic text-xl md:text-3xl text-cream pt-4 leading-snug">
                Not a return to the past.<br />
                A return to what still works.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── REST RITUAL — editorial card grid with product imagery ───────── */}
      {stepPreview.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-hairline bg-bone">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <p className="overline mb-4 text-terracotta">The Rest Ritual · Coming December 2026</p>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light tracking-tight text-ink max-w-3xl">
                A ritual for<br />
                <em className="italic text-forest">slowing down.</em>
              </h2>
            </div>
            <Link
              to="/rituals/rest"
              data-testid="rest-ritual-cta"
              className="hidden md:inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-terracotta link-underline"
            >
              Join the waitlist <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {stepPreview.map((p, idx) => (
              <Link
                key={p.slug}
                to="/rituals/rest"
                data-testid={`rest-step-${p.stage.toLowerCase()}`}
                className="group block"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-clay mb-4">
                  <img
                    src={p.cover_image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 font-mono text-[0.62rem] tracking-[0.22em] uppercase bg-cream px-2 py-1 text-ink">
                    0{idx + 1}
                  </span>
                  <span className="absolute top-3 right-3 font-mono text-[0.62rem] tracking-[0.22em] uppercase bg-terracotta px-2 py-1 text-cream">
                    Soon
                  </span>
                </div>
                <p className="font-mono text-[0.65rem] tracking-[0.22em] uppercase text-terracotta mb-1">
                  — {p.stage}
                </p>
                <h3 className="font-display text-lg md:text-xl text-ink leading-tight group-hover:italic transition-all">
                  {p.name}
                </h3>
                <p className="text-xs text-stone font-mono tracking-wider mt-1">
                  Waitlist · Dec 2026
                </p>
              </Link>
            ))}
          </div>

          <div className="mt-10 md:hidden">
            <Link
              to="/rituals/rest"
              className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-terracotta link-underline"
            >
              Join the waitlist <ArrowUpRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* ───────── FROM INSIDE — image + 4 cards ───────── */}
      <section className="relative border-t border-hairline">
        {/* image strip above */}
        <div className="aspect-[16/8] md:aspect-[16/6] overflow-hidden">
          <img src={BATH_SCENE} alt="A warm evening bath" className="w-full h-full object-cover" />
        </div>

        <div className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-bone">
          <div className="max-w-5xl mb-14">
            <p className="overline mb-6 text-terracotta">From inside · From outside</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light leading-[0.98] tracking-[-0.02em] text-ink max-w-3xl">
              Wellness isn't just<br />
              <em className="italic text-forest">one category.</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 md:gap-6">
            {[
              { k: "Topical", v: "Washes, butters, mists — composed to meet the skin and the senses.", bg: "bg-clay", accent: "text-terracotta" },
              { k: "Ingestible", v: "Teas and sachets — composed to meet the body on the inside.", bg: "bg-cream", accent: "text-forest" },
              { k: "Sensory", v: "A pillow mist, a bath, the quiet of a well-made object.", bg: "bg-terracotta/15", accent: "text-terracotta" },
              { k: "Ritual", v: "Simple sequences, repeated. The practice is the medicine.", bg: "bg-forest/10", accent: "text-forest" },
            ].map((c) => (
              <div key={c.k} className={`${c.bg} p-8 md:p-10`}>
                <p className={`font-mono text-xs tracking-[0.22em] uppercase mb-4 ${c.accent}`}>— {c.k}</p>
                <p className="font-display text-xl md:text-2xl text-ink leading-tight tracking-tight mb-3">
                  {c.k}.
                </p>
                <p className="text-sm md:text-base text-charcoal font-light leading-[1.7]">{c.v}</p>
              </div>
            ))}
          </div>
          <p className="mt-14 font-display italic text-2xl md:text-3xl text-ink max-w-2xl">
            One considered approach to everyday wellbeing.
          </p>
        </div>
      </section>

      {/* ───────── JOURNAL — editorial card grid with covers ───────── */}
      {articles.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-hairline bg-cream">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14 items-end">
            <div className="lg:col-span-6">
              <p className="overline mb-4 text-terracotta">From the journal</p>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-light tracking-tight text-ink">
                Field notes,<br />
                <em className="italic text-forest">quietly kept.</em>
              </h2>
            </div>
            <p className="lg:col-span-5 lg:col-start-8 text-base md:text-lg text-charcoal font-light leading-relaxed">
              Essays and notes on sleep, nourishment, movement and the small
              practices that quietly hold a life together.
            </p>
          </div>

          {/* Feature + grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            {articles.slice(0, 1).map((a) => (
              <Link
                key={a.slug}
                to={`/journal/${a.slug}`}
                data-testid={`article-feature-${a.slug}`}
                className="lg:col-span-7 group block"
              >
                <div className="aspect-[4/3] overflow-hidden bg-clay mb-5">
                  <img
                    src={a.cover_image}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="overline text-terracotta mb-2">{a.category}</p>
                <h3 className="font-display text-2xl md:text-4xl lg:text-5xl text-ink leading-tight tracking-tight group-hover:italic transition-all">
                  {a.title}
                </h3>
                {a.subtitle && (
                  <p className="mt-3 text-charcoal font-light max-w-xl">{a.subtitle}</p>
                )}
                <p className="mt-4 overline text-stone">{a.read_time}</p>
              </Link>
            ))}

            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
              {articles.slice(1, 4).map((a) => (
                <Link
                  key={a.slug}
                  to={`/journal/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group grid grid-cols-5 gap-4 items-start"
                >
                  <div className="col-span-2 aspect-[4/5] overflow-hidden bg-clay">
                    <img
                      src={a.cover_image}
                      alt={a.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="col-span-3">
                    <p className="overline text-terracotta mb-1">{a.category}</p>
                    <h3 className="font-display text-base md:text-lg text-ink leading-tight tracking-tight group-hover:italic transition-all">
                      {a.title}
                    </h3>
                    <p className="mt-2 overline text-stone">{a.read_time}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-14">
            <Link
              to="/journal"
              data-testid="journal-index-cta"
              className="inline-flex items-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-terracotta link-underline"
            >
              All essays <ArrowUpRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* ───────── CLOSING QUOTE PANEL with image overlay ───────── */}
      <section className="relative overflow-hidden border-t border-forest/40 bg-forest text-cream">
        <div className="absolute inset-0 opacity-25">
          <img src={BATH_SCENE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest/70" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-16 py-32 md:py-48">
          <div className="max-w-4xl">
            <p className="overline text-terracotta-light mb-8">A standing instruction</p>
            <p className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-light text-cream">
              "Build the rest first.<br />
              <em className="italic text-terracotta-light">Then build the day around it.</em>"
            </p>
            <p className="overline text-cream/60 mt-10">— Stillform</p>
          </div>
        </div>
      </section>
    </div>
  );
}
